import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxCsvParser } from 'ngx-csv-parser';
import { Subject } from 'rxjs';
import { AntennaModel } from 'src/app/models/antennaModel';
import { ImportFormat } from 'src/app/models/importFormat';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { SelectAntennaPatternService } from 'src/app/services/select-antenna-pattern.service';
import { UiBlockService } from 'src/app/services/ui-block.service';

@Component({
  selector: 'app-add-antenna-model',
  templateUrl: './add-antenna-model.component.html',
  styleUrls: ['../../app.component.scss'],
  standalone: false
})
export class AddAntennaModelComponent {

  private static readonly ERROR_PARSING_MESSAGE: Function = (line: number): string => {
    return "The inserted file is not valid. Error encountered in line " + (line + 1) + ".\n"
      + "The file should be in the format:\n"
      + "Header (only one line):\n"
      + "     Elevation [deg];Gain [dB]\n"
      + "Content:\n"
      + "     -90.974;-31.8841\n"
      + "     -90.6782;-31.5014\n"
      + "     etc; etc";
  }


  readonly importFormat: ImportFormat = ImportFormat.CSV_FORMAT;

  @Input()
  editModeAntennaName: string = null;

  @Input()
  antennaModel: AntennaModel;

  onClose: Subject<AntennaModel>;

  constructor(
    public bsModalRef: BsModalRef,
    public bsModalService: BsModalService,
    public antennaPatternService: SelectAntennaPatternService,
    private uiBlock: UiBlockService,
    private modalService: ModalControlService,
    private ngxCsvParser: NgxCsvParser
  ) {
  }

  ngOnInit(): void {
    this.onClose = new Subject();
  }

  editMode(): boolean {
    return this.editModeAntennaName != null && this.editModeAntennaName != "";
  }

  noChangesMade(): boolean {
    return this.antennaModel.modelName == this.editModeAntennaName && this.antennaModel.antennaFile == null;
  }

  nameExists(): boolean {
    for (const antennaModelIte of this.antennaPatternService.antennaModels) {
      if (this.editMode() && antennaModelIte.modelName == this.editModeAntennaName) {
        continue;
      }
      if (this.antennaModel.modelName == antennaModelIte.modelName) {
        return true;
      }
    }
    return false;
  }

  saveFile(event: any) {
    const file = <File>event.target.files[0];
    this.antennaModel.antennaFile = file;
    this.validateFile(file);
  }

  fileIsUploaded() {
    return this.editMode() || this.antennaModel.antennaFile != null;
  }

  async saveButton() {
    if (!this.fileIsUploaded()) {
      this.modalService.showMesssageModal("Add Antenna Model", "Please upload an Antenna Pattern file.");
      return;
    }
    this.uiBlock.startBlock("Saving Antenna Model...")
    try {
      let savedAntennaModel: AntennaModel;
      if (this.editMode()) {
        if (this.noChangesMade()) {
          this.onClose.next(null);
          return;
        }
        savedAntennaModel = await this.updateAntennaModel();
      } else {
        savedAntennaModel = await this.saveAntennaModel();
      }
      if (savedAntennaModel == null) {
        throw new Error("Error saving Antenna Model");
      }
      this.onClose.next(savedAntennaModel);
      this.modalService.showMesssageModal("Antenna Model Selection", "Antenna Model saved sucessfully.")
    } catch (e) {
      this.modalService.showMesssageModal("Error", "There was an error saving the Antenna Model.")
      this.onClose.next(null);
    } finally {
      this.bsModalRef.hide();
      this.uiBlock.stopBlock();
    }
  }

  async saveAntennaModel(): Promise<AntennaModel> {
    return await this.antennaPatternService.saveAntennaModel(AntennaModel.mapToFormData(this.antennaModel));
  }

  async updateAntennaModel(): Promise<AntennaModel> {
    return await this.antennaPatternService.updateAntennaModel(AntennaModel.mapToFormData(this.antennaModel));
  }

  close() {
    this.onClose.next(null);
    this.bsModalRef.hide();
  }


  validateFile(file: File): boolean {
    if (!this.ngxCsvParser.isCSVFile(file)) {
      this.modalService.showMesssageModal("Error", 'The inserted file has an invalid extension. Insert a "csv" file.');
      this.resetFileInput();
      return;
    }

    this.ngxCsvParser
      .parse(file, { header: false, delimiter: ";" }).subscribe({
        next: (rows: Array<any>) => {
          for (let i: number = 0; i < rows.length; i++) {
            if (rows[i][rows[i].length - 1] === "") { // in cases where the lines in the file end with the separator
              rows[i].pop();
            }
            if (rows[i].length != 2) {
              this.modalService.showFormattedTextMesssageModal("Error", AddAntennaModelComponent.ERROR_PARSING_MESSAGE(i));
              this.resetFileInput()
              return;
            }
            const isHeaderLine: boolean = i == 0; // one line

            for (const value of rows[i]) {
              if ((isHeaderLine && !isNaN(Number(value))) || (!isHeaderLine && isNaN(Number(value)))) {
                this.modalService.showFormattedTextMesssageModal("Error", AddAntennaModelComponent.ERROR_PARSING_MESSAGE(i));
                this.resetFileInput();
                return;
              }
            }
          }
        },
        error: error => {
          this.modalService.showMesssageModal("Error", "The inserted file is not valid.");
          this.resetFileInput();
        }
      })
  }

  private resetFileInput() {
    this.antennaModel.antennaFile = null;
    (<HTMLInputElement>document.getElementById("AntennaFileId")).value = null;
  }




}
